/**
 * לקוח ה-API לניהול מוצרים וואריאנטים — TASKS 3.3.
 */
import type { AdminApiError } from './adminApi.js';

export const PRICING_TYPES = ['unit', 'per_meter'] as const;
export type PricingType = (typeof PRICING_TYPES)[number];

/** ⛔ תואם בכוונה את מה שהשרת מחזיר ברשימה — בלי description/spec_html (ביצועים) */
export interface ProductListItem {
  id: number;
  sku: string;
  category_id: number;
  category_name: string;
  name: string;
  pricing_type: PricingType;
  base_price: string;
  unit_label: string | null;
  price_is_estimate: number;
  price_on_request: number;
  sort_order: number;
  is_active: number;
  created_at: string;
  updated_at: string;
}

export interface ProductDetail extends ProductListItem {
  description: string | null;
  spec_html: string | null;
  min_order_qty: string | null;
  qty_step: string | null;
}

export interface Variant {
  id: number;
  product_id: number;
  sku: string;
  label: string;
  price: string | null;
  /** ⭐ "מחיר לפי בקשה" ברמת הואריאנט — מקביל אורתוגונלי לדגל ברמת המוצר. */
  price_on_request: number;
  attrs: unknown;
  sort_order: number;
  is_active: number;
}

export interface ProductListParams {
  page?: number;
  categoryId?: number;
  isActive?: boolean;
  q?: string;
}

async function parseErrorBody(res: Response): Promise<AdminApiError> {
  try {
    const body = (await res.json()) as { error?: { code?: string; message?: string } };
    return {
      status: res.status,
      code: body.error?.code ?? 'UNKNOWN_ERROR',
      message: body.error?.message ?? 'אירעה שגיאה לא צפויה.',
    };
  } catch {
    return { status: res.status, code: 'UNKNOWN_ERROR', message: 'אירעה שגיאה לא צפויה.' };
  }
}

export async function listProducts(
  params: ProductListParams = {},
): Promise<{ products: ProductListItem[]; page: number; pageSize: number; total: number }> {
  const search = new URLSearchParams();
  if (params.page) search.set('page', String(params.page));
  if (params.categoryId) search.set('categoryId', String(params.categoryId));
  if (params.isActive !== undefined) search.set('isActive', String(params.isActive));
  if (params.q) search.set('q', params.q);

  const res = await fetch(`/api/admin/products?${search.toString()}`, {
    credentials: 'same-origin',
  });
  if (!res.ok) throw await parseErrorBody(res);
  return (await res.json()) as {
    products: ProductListItem[];
    page: number;
    pageSize: number;
    total: number;
  };
}

export async function getProduct(
  id: number,
): Promise<{ product: ProductDetail; variants: Variant[] }> {
  const res = await fetch(`/api/admin/products/${id}`, { credentials: 'same-origin' });
  if (!res.ok) throw await parseErrorBody(res);
  return (await res.json()) as { product: ProductDetail; variants: Variant[] };
}

export interface ProductInput {
  categoryId: number;
  name: string;
  description?: string;
  specHtml?: string;
  pricingType: PricingType;
  basePrice: number;
  unitLabel?: string;
  minOrderQty?: number;
  qtyStep?: number;
  priceIsEstimate?: boolean;
  /** ⭐ "מחיר לפי בקשה" — מקביל אורתוגונלי ל-`priceIsEstimate`. */
  priceOnRequest?: boolean;
}

export async function createProduct(input: ProductInput): Promise<{ id: number; sku: string }> {
  const res = await fetch('/api/admin/products', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw await parseErrorBody(res);
  return (await res.json()) as { id: number; sku: string };
}

export async function updateProduct(
  id: number,
  input: Partial<ProductInput> & { isActive?: boolean },
): Promise<void> {
  const res = await fetch(`/api/admin/products/${id}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw await parseErrorBody(res);
}

export async function deleteProduct(id: number): Promise<void> {
  const res = await fetch(`/api/admin/products/${id}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  if (!res.ok) throw await parseErrorBody(res);
}

export interface VariantInput {
  suffix: string;
  label: string;
  price?: number;
  priceOnRequest?: boolean;
}

export async function createVariant(
  productId: number,
  input: VariantInput,
): Promise<{ id: number; sku: string }> {
  const res = await fetch(`/api/admin/products/${productId}/variants`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw await parseErrorBody(res);
  return (await res.json()) as { id: number; sku: string };
}

export async function updateVariant(
  productId: number,
  variantId: number,
  input: { label?: string; price?: number | null; isActive?: boolean; priceOnRequest?: boolean },
): Promise<void> {
  const res = await fetch(`/api/admin/products/${productId}/variants/${variantId}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw await parseErrorBody(res);
}

export async function deleteVariant(productId: number, variantId: number): Promise<void> {
  const res = await fetch(`/api/admin/products/${productId}/variants/${variantId}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  if (!res.ok) throw await parseErrorBody(res);
}
